import { Link } from "react-router-dom";
import { Instagram, Facebook, Twitter, Youtube, Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { useSettings } from "../lib/SettingsContext";
import { LOGO_LIGHT } from "../lib/brand";
import InstagramFeed from "./InstagramFeed";

export default function Footer() {
    const { settings } = useSettings();

    return (
        <footer data-testid="site-footer">
            {/* "Bring your idea to life" CTA band - pink tint */}
            <section className="bg-blush-pale border-t border-blush/15">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 text-center">
                    <p className="text-xs tracking-[0.35em] uppercase text-blush mb-4 font-medium">Let's create something sweet</p>
                    <h2 className="font-display text-3xl md:text-5xl text-ink mb-8 leading-tight">
                        Bring your idea<br className="hidden md:block" />
                        <span className="italic text-blush"> to life</span>
                    </h2>
                    <Link
                        to="/contact"
                        className="btn-pink"
                        data-testid="footer-enquire-btn"
                    >
                        Enquire Now <ArrowRight size={14} />
                    </Link>
                </div>
            </section>

            {/* Instagram feed band */}
            <section className="bg-[#FFF7FB]">
                <div className="px-4 sm:px-6 lg:px-10 py-14 md:py-16 max-w-screen-2xl mx-auto">
                    <div className="flex items-end justify-between mb-8">
                        <div>
                            <p className="text-xs tracking-[0.3em] uppercase text-blush mb-2 font-medium">Follow our work</p>
                            <h3 className="font-display text-2xl md:text-3xl text-ink">@cakecentral</h3>
                        </div>
                        <a
                            href={settings.instagram_url}
                            target="_blank"
                            rel="noreferrer"
                            className="hidden md:inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ink hover:text-blush transition-colors"
                            data-testid="footer-instagram-link"
                        >
                            <Instagram size={16} /> View on Instagram
                        </a>
                    </div>
                    <InstagramFeed />
                </div>
            </section>

            {/* Compact black footer */}
            <section className="bg-[#111111] text-white border-t-2 border-blush">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <Link to="/" className="inline-flex items-center" data-testid="footer-logo">
                            <img src={LOGO_LIGHT} alt="Cake Central" className="h-10 md:h-12 w-auto" />
                        </Link>

                        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-8 text-sm">
                            <span className="inline-flex items-center gap-2 text-white/90">
                                <MapPin size={14} className="text-blush" /> {settings.address}
                            </span>
                            <a
                                href={`mailto:${settings.email}`}
                                className="inline-flex items-center gap-2 text-white hover:text-blush transition-colors"
                                data-testid="footer-email"
                            >
                                <Mail size={14} className="text-blush" /> {settings.email}
                            </a>
                            <a
                                href={`tel:${settings.phone?.replace(/\s/g, "")}`}
                                className="inline-flex items-center gap-2 text-white hover:text-blush transition-colors"
                                data-testid="footer-phone"
                            >
                                <Phone size={14} className="text-blush" /> {settings.phone}
                            </a>
                        </div>

                        <div className="flex items-center gap-4">
                            {settings.instagram_url && (
                                <a href={settings.instagram_url} target="_blank" rel="noreferrer" className="text-white/80 hover:text-blush transition-colors">
                                    <Instagram size={18} />
                                </a>
                            )}
                            {settings.facebook_url && (
                                <a href={settings.facebook_url} target="_blank" rel="noreferrer" className="text-white/80 hover:text-blush transition-colors">
                                    <Facebook size={18} />
                                </a>
                            )}
                            {settings.twitter_url && (
                                <a href={settings.twitter_url} target="_blank" rel="noreferrer" className="text-white/80 hover:text-blush transition-colors">
                                    <Twitter size={18} />
                                </a>
                            )}
                            {settings.youtube_url && (
                                <a href={settings.youtube_url} target="_blank" rel="noreferrer" className="text-white/80 hover:text-blush transition-colors">
                                    <Youtube size={18} />
                                </a>
                            )}
                        </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-white/60">
                        <p>© {new Date().getFullYear()} Cake Central. All rights reserved.</p>
                        <div className="flex gap-6">
                            <Link to="/admin/login" className="hover:text-white transition-colors" data-testid="footer-admin-link">Admin</Link>
                        </div>
                    </div>
                </div>
            </section>
        </footer>
    );
}
